// src/components/Layout/Sidebar.js
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import UpgradeModal from '../UpgradeModal';
import './Sidebar.css';

const PUBLIC_IMAGE_ROOT = process.env.PUBLIC_URL || '';
// Note: Browsers handle spaces in URLs, but %20 encoding is also valid
const LOGO_LIGHT = `${PUBLIC_IMAGE_ROOT}/images2/Logos AWD/Horizontal/Colored_no_background.png`;
const LOGO_DARK = `${PUBLIC_IMAGE_ROOT}/images2/Logos AWD/Horizontal/Colored_background.png`;
const LOGO_ICON = `${PUBLIC_IMAGE_ROOT}/images2/Logos AWD/Horizontal/Only_Logo.png`;

function Sidebar({ user, state, onToggleSidebar, onToggleCustomize, onToggleDarkMode }) {
  const { logout } = useAuth();
  const { darkMode } = useTheme();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.lucide && typeof window.lucide.createIcons === 'function') {
      window.lucide.createIcons();
    }
  }, [state]);

  // Ensure icons are created on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.lucide && typeof window.lucide.createIcons === 'function') {
      setTimeout(() => {
        window.lucide.createIcons();
      }, 100);
    }
  }, []);

  const handleLogout = () => {
    logout();
    // Redirection vers la landing page après déconnexion
    window.location.href = "https://www.automotive-database.com/";
  };

  const goToHome = () => {
    // Redirection vers la landing page sans déconnexion
    window.location.href = "https://www.automotive-database.com/";
  };

  // Determine which logo to show based on sidebar state and dark mode
  const getLogoSrc = () => {
    if (state === 'collapsed') {
      console.log('Using LOGO_ICON:', LOGO_ICON);
      return LOGO_ICON;
    }
    const logo = darkMode ? LOGO_DARK : LOGO_LIGHT;
    console.log('Using logo:', logo);
    return logo;
  };

  const handleImageError = (e) => {
    console.error('Failed to load logo:', e.target.src);
    console.log('Available logos:', { LOGO_LIGHT, LOGO_DARK, LOGO_ICON });
    console.log('Current state:', state);
    console.log('Dark mode:', darkMode);
  };

  return (
    <div className={`sidebar ${state}`}>
      <div className="sidebar-header">
        <div className="logo">
          <button onClick={goToHome} className="logo-button" title="Go to automotive-database.com">
            <img
              src={getLogoSrc()}
              alt="AWD Database"
              className={`logo-image ${state === 'open' ? 'logo-full' : 'logo-icon'}`}
              onError={handleImageError}
            />
          </button>
        </div>
        <button
          className="sidebar-toggle-btn"
          onClick={onToggleSidebar}
          title={state === 'open' ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-label={state === 'open' ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {state === 'open' ? (
              // Arrow pointing left (collapse)
              <path d="M15 18l-6-6 6-6" />
            ) : (
              // Arrow pointing right (expand)
              <path d="M9 18l6-6-6-6" />
            )}
          </svg>
        </button>
      </div>
      <div className="sidebar-user">
        <div className="user-avatar">
          {user?.name ?
            user.name.split(' ').map(word => word.charAt(0).toUpperCase()).slice(0, 2).join('')
            : 'U'}
        </div>
        {state === 'open' && (
          <div className="user-info">
            <div className="user-name">{user?.name || 'Utilisateur'}</div>
            <div className="user-plan-info">
              <span className="user-plan">{user?.plan || 'Free Plan'}</span>
              <button
                className="sidebar-upgrade-btn"
                onClick={() => setShowUpgradeModal(true)}
                title="Upgrade your plan"
              >
                Upgrade
              </button>
            </div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        <ul>
          <li>
            <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'active' : ''} end>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="layout-dashboard" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Dashboard</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Dashboard</span>}
            </NavLink>
          </li>
          <li>
            <NavLink to="/dashboard/database" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="database" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Database</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Database</span>}
            </NavLink>
          </li>
          <li>
            <NavLink to="/dashboard/conferences" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="calendar-days" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Conferences</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Conferences</span>}
            </NavLink>
          </li>
          <li>
            <NavLink to="/dashboard/patents" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="file-text" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Patents</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Patents</span>}
            </NavLink>
          </li>
          <li>
            <NavLink to="/dashboard/favorites" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="star" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Favorites</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Favorites</span>}
            </NavLink>
          </li>
          <li>
            <NavLink to="/dashboard/history" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="history" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">History</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">History</span>}
            </NavLink>
          </li>
        </ul>
      </nav>

      <div className="sidebar-footer">
        <ul>
          <li>
            <button
              className="customize-btn"
              type="button"
              onClick={onToggleCustomize}
              title="Customize"
            >
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="sliders" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Customize</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Customize</span>}
            </button>
          </li>
          <li>
            <button
              className="darkmode-btn"
              type="button"
              onClick={onToggleDarkMode}
              title={darkMode ? 'Switch to bright mode' : 'Switch to dark mode'}
            >
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide={darkMode ? 'sun' : 'moon'} className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">{darkMode ? 'Bright mode' : 'Dark mode'}</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">{darkMode ? 'Bright mode' : 'Dark mode'}</span>}
            </button>
          </li>
          <li className="sidebar-divider"></li>
          <li>
            <NavLink to="/dashboard/settings" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="settings" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Settings</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Settings</span>}
            </NavLink>
          </li>
          <li>
            <button className="logout-btn" type="button" onClick={handleLogout}>
              <span className="nav-icon nav-tooltip-parent">
                <i data-lucide="log-out" className="nav-lucide-icon" aria-hidden="true"></i>
                {state === 'collapsed' && (
                  <span className="nav-tooltip-popup">Logout</span>
                )}
              </span>
              {state === 'open' && <span className="nav-text">Logout</span>}
            </button>
          </li>
        </ul>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
}

export default Sidebar;
